// PatFormDrawer — the create-a-personal-access-token drawer (ADR-004).
// A SlideOver create per the form-surface taxonomy (ADR-038). Pick a project FIRST
// → the shared capability editor greys the read-tier keys the user does NOT hold in THAT
// project (the future request-a-grant flow will let them ask for those). Label +
// expiry + the picked reads complete it. A project change drops the picked reads the
// user does not hold THERE and says so (never a ticked key nobody can clear). There is
// no edit mode — a token's shape is frozen at creation (rename is a separate inline
// action). The drawer is always mounted: DrawerForm re-seeds it on every open (a
// create — the seed is the empty token, the first project picked).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { useMe } from '../../hooks/useMe.js';
import { useResetOnOpen } from '../../hooks/useResetOnOpen.js';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Picker } from '../../components/patterns/Picker.js';
import { codeSource } from '../../hooks/useOptions.js';
import { useProjectOptionRows } from '../../components/layout/projectRows.js';
import { Alert } from '../../components/ui/Alert.js';
import { CapabilityEditor } from '../../components/patterns/capabilities/CapabilityEditor.js';
import { ALL_ENTITLEMENTS_ON } from '../../components/patterns/capabilities/presentation.js';
import { PAT_GRANTABLE_KEYS, PAT_EXPIRY_CHOICES, type PatExpiryChoice } from '@ems-app/schemas';
import { unmetRequires } from '@ems-app/schemas/permissions';

export interface PatSubmit {
  label: string;
  project_id: string;
  permissions: string[];
  expires_in: PatExpiryChoice;
}

const formSchema = z.object({
  label: z.string().trim().min(1).max(64),
  project_id: z.string().min(1),
  expires_in: z.enum(PAT_EXPIRY_CHOICES),
  permissions: z.array(z.string()).min(1),
});
type PatFormValues = z.infer<typeof formSchema>;

interface PatFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: PatSubmit) => Promise<void>;
  onSuccess: () => void;
}

export function PatFormDrawer({ open, onClose, onSubmit, onSuccess }: PatFormDrawerProps) {
  const { t } = useTranslation('tokens');
  const { t: tc } = useTranslation('common');
  const { data: me } = useMe();

  // A PAT pins to an ACTIVE project the user belongs to (a token for a suspended
  // project would only ever 403). project-admins hold everything; everyone else is
  // bounded to what they hold in the chosen project.
  const projectRows = useProjectOptionRows((p) => p.status === 'active');
  const projects = useMemo(() => codeSource(`pat.projects:${projectRows.map((r) => r.id).join(',')}`, projectRows), [projectRows]);
  const expiries = useMemo(() => codeSource('pat.expiry', PAT_EXPIRY_CHOICES.map((c) => ({ id: c, label: t(`expiry.${c}`), pickable: true, group: null }))), [t]);
  // What a project change dropped — said under the permissions, until the next change; a new opening starts silent.
  const [dropped, setDropped] = useState<string | null>(null);
  useResetOnOpen(open, (isOpen) => {
    if (isOpen) setDropped(null);
  });

  const shell = useFormShell<PatFormValues>({
    schema: formSchema,
    // Read at every open — a second token never opens with the first one's picks.
    values: () => ({ label: '', project_id: projectRows[0]?.id ?? '', expires_in: '90d', permissions: [] }),
    onSubmit: async (v) => {
      await onSubmit({
        label: v.label,
        project_id: v.project_id,
        permissions: v.permissions,
        expires_in: v.expires_in,
      });
    },
    onSuccess,
  });
  const err = shell.form.formState.errors;

  const projectId = shell.form.watch('project_id');
  const picked = shell.form.watch('permissions');
  // Picked keys whose prerequisite is not picked — the server drops them silently
  // on mint (normalizeGrants), so the drawer says so before submit; the editor's
  // Fix is the one-click cure (never an auto-add).
  const willDrop = useMemo(() => unmetRequires(picked), [picked]);

  // The read-tier keys the user does NOT hold in the chosen project → greyed as
  // "not held". Admins (is_project_admin) hold everything → nothing greyed. The
  // held set from /me is already includes-expanded + entitlement-filtered.
  const heldIn = (pid: string): ReadonlySet<string> | null => {
    const entry = pid ? me?.permissions[pid] : undefined;
    return !entry || entry.is_project_admin ? null : new Set(entry.permissions);
  };
  const disabledKeys = useMemo(() => {
    const held = heldIn(projectId);
    return held === null ? new Set<string>() : new Set(PAT_GRANTABLE_KEYS.filter((k) => !held.has(k)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `heldIn` reads exactly these
  }, [me, projectId]);

  // The parent rule on a list of checks: a project change keeps the picked reads held there, drops the rest — and says it.
  const changeProject = (pid: string) => {
    const held = heldIn(pid);
    const now = shell.form.getValues('permissions');
    const kept = held === null ? now : now.filter((k) => held.has(k));
    if (kept.length !== now.length) shell.form.setValue('permissions', kept, { shouldDirty: true });
    const name = projectRows.find((r) => r.id === pid)?.label ?? '';
    setDropped(kept.length !== now.length ? t('create.permissionsDropped', { count: now.length - kept.length, project: name }) : null);
  };

  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={t('create.title')}
      description={t('create.description')}
      submitLabel={t('create.submit')}
      maxWidth="max-w-xl"
      autoFocusField="label"
    >
      <div>
        <Label htmlFor="pat-label" required>
          {t('create.label')}
        </Label>
        <Input id="pat-label" {...shell.form.register('label')} error={err.label?.message} placeholder={t('create.labelPlaceholder')} />
        <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('create.labelHelp')}</p>
      </div>

      <Controller
        control={shell.form.control}
        name="project_id"
        render={({ field, fieldState }) => (
          <Picker
            source={projects}
            label={t('create.project')}
            required
            value={field.value || null}
            onChange={(v) => {
              field.onChange(v ?? '');
              if (v && v !== field.value) changeProject(v);
            }}
            help={t('create.projectHelp')}
            error={fieldState.error ? tc('form.required') : undefined}
          />
        )}
      />

      <Controller
        control={shell.form.control}
        name="expires_in"
        render={({ field }) => (
          <Picker source={expiries} label={t('create.expiry')} value={field.value} onChange={(v) => field.onChange((v as PatExpiryChoice | null) ?? '90d')} />
        )}
      />

      <Controller
        control={shell.form.control}
        name="permissions"
        render={({ field }) => (
          <div>
            <p className="text-sm font-medium">{t('create.permissions')}</p>
            <p className="mb-2 text-xs text-[var(--color-text-secondary)]">{t('create.permissionsHelp')}</p>
            <CapabilityEditor
              grants={field.value}
              onGrantsChange={field.onChange}
              offeredKeys={PAT_GRANTABLE_KEYS}
              disabledKeys={disabledKeys}
              entitlements={ALL_ENTITLEMENTS_ON}
              headingLevel={4}
            />
            {/* the region exists before its text, so the drop is announced */}
            <p role="status" aria-live="polite" className={dropped ? 'mt-2 text-sm text-[var(--color-text-secondary)]' : 'sr-only'}>
              {dropped}
            </p>
            {picked.length === 0 && <Alert variant="warning" className="mt-2">{t('create.noPermsWarn')}</Alert>}
            {willDrop.length > 0 && (
              <Alert variant="warning" className="mt-2">
                {t('create.willDropWarn', { count: willDrop.length })}
              </Alert>
            )}
          </div>
        )}
      />
    </DrawerForm>
  );
}
